/** Marketing-Landing — Spiel unter /play */

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { AuthCard, type AuthMode } from "./AuthCard";
import { api } from "./api";
import { APP_VERSION, LANDING } from "./content";
import { LegalPanel } from "./LegalPanel";
import { useGame } from "./store";
import { useLayoutMode } from "./useLayoutMode";

type SessionInfo = {
  token: string;
  username: string;
  playAllowed: boolean;
  worldName: string | null;
};

function readAuthBlob(): { token: string; username: string } | null {
  try {
    const raw = JSON.parse(localStorage.getItem("bda-auth") ?? "null") as {
      token?: string;
      username?: string;
    } | null;
    if (raw?.token && raw?.username) return { token: raw.token, username: raw.username };
  } catch {
    /* ignore */
  }
  return null;
}

function authModeFromHash(): AuthMode | null {
  const h = (window.location.hash || "").replace(/^#/, "").toLowerCase();
  if (h === "login" || h === "anmelden") return "login";
  if (h === "register" || h === "registrieren") return "register";
  const q = new URLSearchParams(window.location.search);
  const a = (q.get("auth") || "").toLowerCase();
  if (a === "login" || a === "register") return a;
  return null;
}

function useReveal(threshold = 0.12) {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold, rootMargin: "0px 0px -8% 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return { ref, visible };
}

function RevealSection({
  id,
  className,
  children,
  "aria-labelledby": labelledBy,
}: {
  id?: string;
  className?: string;
  children: ReactNode;
  "aria-labelledby"?: string;
}) {
  const { ref, visible } = useReveal();
  return (
    <section
      id={id}
      ref={ref}
      className={`landing-section${visible ? " landing-section--in" : ""}${className ? ` ${className}` : ""}`}
      aria-labelledby={labelledBy}
    >
      {children}
    </section>
  );
}

function HeroVisual({ states }: { states: typeof LANDING.heroStates }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = window.setInterval(() => setIdx((i) => (i + 1) % states.length), 4200);
    return () => window.clearInterval(t);
  }, [states.length]);

  const active = states[idx];

  return (
    <div className="landing-hero-visual" aria-hidden>
      <div className="landing-hero-visual__frame">
        <div className="landing-hero-visual__bar">
          <span className="landing-hero-visual__dot landing-hero-visual__dot--r" />
          <span className="landing-hero-visual__dot landing-hero-visual__dot--a" />
          <span className="landing-hero-visual__dot landing-hero-visual__dot--g" />
          <span className="landing-hero-visual__bar-title">DISPATCH · LIVE</span>
        </div>
        <div className="landing-hero-visual__map">
          <div className="landing-hero-visual__grid" />
          <span className="landing-hero-visual__pin landing-hero-visual__pin--1" />
          <span className="landing-hero-visual__pin landing-hero-visual__pin--2" />
          <span className="landing-hero-visual__pin landing-hero-visual__pin--3" />
          <span className="landing-hero-visual__radius" />
        </div>
        <div className="landing-hero-visual__feed">
          {states.map((s, i) => (
            <div
              key={s.code}
              className={`landing-hero-visual__row${i === idx ? " landing-hero-visual__row--active" : ""}`}
            >
              <span className="landing-hero-visual__code">{s.code}</span>
              <span className="landing-hero-visual__label">{s.label}</span>
              <span className="landing-hero-visual__unit">{s.unit}</span>
              <span className="landing-hero-visual__status">{s.status}</span>
            </div>
          ))}
        </div>
        <div className="landing-hero-visual__pulse" key={active.code}>
          <span className="landing-hero-visual__pulse-code">{active.code}</span>
          <span>{active.label}</span>
        </div>
      </div>
    </div>
  );
}

function ScreenPlaceholder({ id, title }: { id: string; title: string }) {
  return (
    <div className={`landing-screen landing-screen--${id}`} aria-label={title}>
      <div className="landing-screen__chrome">
        <span />
        <span />
        <span />
      </div>
      <div className="landing-screen__body">
        {id === "map" && (
          <>
            <div className="landing-screen__map-grid" />
            <span className="landing-screen__map-pin landing-screen__map-pin--a" />
            <span className="landing-screen__map-pin landing-screen__map-pin--b" />
            <span className="landing-screen__map-pin landing-screen__map-pin--c" />
            <span className="landing-screen__map-circle" />
          </>
        )}
        {id === "dispatch" && (
          <ul className="landing-screen__dispatch-list">
            <li><span>F1</span> Wohnungsbrand <em>Alarmiert</em></li>
            <li><span>TH</span> VU eingeklemmt <em>Anfahrt</em></li>
            <li><span>POL</span> Personensuche <em>Lage</em></li>
          </ul>
        )}
        {id === "station" && (
          <div className="landing-screen__station">
            <div className="landing-screen__station-bar" />
            <div className="landing-screen__station-row" />
            <div className="landing-screen__station-row landing-screen__station-row--short" />
            <div className="landing-screen__station-fleet">
              <span /><span /><span /><span />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function LandingPage() {
  const layout = useLayoutMode();
  const setAuth = useGame((s) => s.setAuth);
  const logout = useGame((s) => s.logout);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState<AuthMode | null>(() => authModeFromHash());
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const L = LANDING;

  useEffect(() => {
    document.documentElement.dataset.landing = "1";
    return () => {
      delete document.documentElement.dataset.landing;
    };
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!authOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [authOpen]);

  useEffect(() => {
    let cancelled = false;
    const blob = readAuthBlob();
    if (!blob) {
      setSession(null);
      setSessionLoading(false);
      return;
    }
    (async () => {
      try {
        const me = await api.me(blob.token);
        if (cancelled) return;
        setAuth(blob.token, me.user.username, me.user);
        let worldName: string | null = null;
        if (me.user.playAllowed || me.user.isAdmin) {
          try {
            const { worlds, activeWorldId } = await api.worlds(blob.token);
            const active =
              worlds.find((w) => w.id === activeWorldId) ?? worlds[0] ?? null;
            worldName = active?.cityName ?? null;
          } catch {
            /* ignore */
          }
        }
        if (!cancelled) {
          setSession({
            token: blob.token,
            username: me.user.username,
            playAllowed: !!(me.user.playAllowed || me.user.isAdmin),
            worldName,
          });
        }
      } catch {
        logout();
        if (!cancelled) setSession(null);
      } finally {
        if (!cancelled) setSessionLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setAuth, logout]);

  const openAuth = useCallback((mode: AuthMode) => {
    setAuthOpen(mode);
    setNavOpen(false);
    window.history.replaceState(null, "", mode === "login" ? "/#login" : "/#register");
  }, []);

  const closeAuth = useCallback(() => {
    setAuthOpen(null);
    window.history.replaceState(null, "", "/");
  }, []);

  function onAuthed(token: string, username: string, user: import("./api").AuthUser) {
    setAuth(token, username, user);
    window.location.assign("/play");
  }

  function signOut() {
    logout();
    setSession(null);
    openAuth("login");
  }

  function scrollTo(id: string) {
    setNavOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const loggedIn = !!session;

  const headerCtas = sessionLoading ? (
    <span className="muted small">…</span>
  ) : loggedIn ? (
    <>
      <span className="muted small landing-user">
        {session.username}
        {session.worldName ? ` · ${session.worldName}` : ""}
      </span>
      {session.playAllowed ? (
        <a className="btn primary" href="/play">
          {L.ctaContinue}
        </a>
      ) : (
        <a className="btn" href="/play">
          Status prüfen
        </a>
      )}
      <button type="button" className="btn ghost" onClick={signOut}>
        Abmelden
      </button>
    </>
  ) : (
    <>
      <button type="button" className="btn ghost landing-cta-nav" onClick={() => openAuth("login")}>
        Anmelden
      </button>
      <button type="button" className="btn landing-cta-nav" onClick={() => openAuth("register")}>
        Registrieren
      </button>
      <button type="button" className="btn primary landing-cta-nav" onClick={() => openAuth("login")}>
        Spielen
      </button>
    </>
  );

  return (
    <div className="landing" data-layout={layout}>
      <div className="landing-bg" aria-hidden>
        <div className="landing-map-glow" />
        <div className="landing-map-roads" />
        <div className="landing-map-pins">
          <span className="landing-pin landing-pin--fw" />
          <span className="landing-pin landing-pin--rd" />
          <span className="landing-pin landing-pin--pol" />
          <span className="landing-pin landing-pin--inc" />
        </div>
        <div className="landing-scan" />
      </div>
      <div className="landing-grid" aria-hidden />

      <header className={`landing-header${scrolled ? " landing-header--scrolled" : ""}`}>
        <div className="landing-header__inner">
          <a className="landing-brand-block" href="/" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
            <strong className="landing-brand">{L.brand}</strong>
            <span className="landing-brand-sub muted small">
              {L.shortBrand} · Alpha {APP_VERSION}
            </span>
          </a>

          <button
            type="button"
            className="landing-nav-toggle"
            aria-expanded={navOpen}
            aria-controls="landing-nav-menu"
            onClick={() => setNavOpen((o) => !o)}
          >
            {navOpen ? "✕" : "☰"}
          </button>

          <nav
            id="landing-nav-menu"
            className={`landing-nav-links${navOpen ? " landing-nav-links--open" : ""}`}
            aria-label="Seitenabschnitte"
          >
            {L.nav.map((n) => (
              <button key={n.id} type="button" className="landing-nav-link" onClick={() => scrollTo(n.id)}>
                {n.label}
              </button>
            ))}
          </nav>

          <nav className="landing-nav-cta" aria-label="Konto">
            {headerCtas}
          </nav>
        </div>
      </header>

      <section className="landing-hero" aria-labelledby="landing-hero-h">
        <div className="landing-hero__copy">
          <p className="landing-kicker">{L.kicker}</p>
          <h1 id="landing-hero-h" className="landing-title">
            <span className="landing-title-brand">{L.brand}</span>
            <span className="landing-title-line">{L.title}</span>
          </h1>
          <p className="landing-lede">{L.lede}</p>
          <div className="landing-cta">
            {loggedIn && session?.playAllowed ? (
              <>
                <a className="btn primary landing-cta-main" href="/play">
                  {L.ctaContinue}
                  {session.worldName ? ` · ${session.worldName}` : ""}
                </a>
                <p className="muted small">Angemeldet als {session.username}</p>
              </>
            ) : (
              <>
                <button type="button" className="btn primary landing-cta-main" onClick={() => openAuth("login")}>
                  Spielen
                </button>
                <button type="button" className="btn" onClick={() => openAuth("register")}>
                  Registrieren
                </button>
                <button type="button" className="btn ghost" onClick={() => openAuth("login")}>
                  Anmelden
                </button>
                <p className="muted small">Alpha · Desktop zuerst · Login ist der Einstieg</p>
              </>
            )}
          </div>
        </div>
        <HeroVisual states={L.heroStates} />
      </section>

      <RevealSection id="features" className="landing-features" aria-labelledby="landing-features-h">
        <div className="landing-section-head">
          <h2 id="landing-features-h" className="landing-section-title">
            {L.featuresTitle}
          </h2>
          <p className="landing-section-sub muted">{L.featuresSubtitle}</p>
        </div>
        <ul className="landing-feature-highlight">
          {L.featuresHighlight.map((f) => (
            <li key={f.id} className={`landing-feature-card landing-feature-card--${f.id}`}>
              <span className="landing-feature-tag">{f.tag}</span>
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </li>
          ))}
        </ul>
        <ul className="landing-feature-grid">
          {L.features.map((f) => (
            <li key={f.title}>
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </li>
          ))}
        </ul>
      </RevealSection>

      <RevealSection id="ablauf" className="landing-how" aria-labelledby="landing-how-h">
        <div className="landing-section-head">
          <h2 id="landing-how-h" className="landing-section-title">
            {L.howTitle}
          </h2>
          <p className="landing-section-sub muted">{L.howSubtitle}</p>
        </div>
        <ol className="landing-how-list">
          {L.howSteps.map((s) => (
            <li key={s.step}>
              <span className="landing-how-step" aria-hidden>
                {s.step}
              </span>
              <div>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </RevealSection>

      <RevealSection id="einblicke" className="landing-screens" aria-labelledby="landing-screens-h">
        <div className="landing-section-head">
          <h2 id="landing-screens-h" className="landing-section-title">
            {L.screensTitle}
          </h2>
          <p className="landing-section-sub muted">{L.screensSubtitle}</p>
        </div>
        <ul className="landing-screen-grid">
          {L.screens.map((s) => (
            <li key={s.id}>
              <ScreenPlaceholder id={s.id} title={s.title} />
              <h3>{s.title}</h3>
              <p className="muted">{s.caption}</p>
            </li>
          ))}
        </ul>
      </RevealSection>

      <RevealSection id="saison" className="landing-season" aria-labelledby="landing-season-h">
        <div className="landing-season__inner">
          <div>
            <h2 id="landing-season-h" className="landing-section-title">
              {L.seasonTitle}
            </h2>
            <p className="landing-season__body">{L.seasonBody}</p>
            <ul className="landing-season__bullets">
              {L.seasonBullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
            {loggedIn && session?.playAllowed && (
              <a className="btn primary" href="/play?panel=saison">
                Saison-Ranking öffnen
              </a>
            )}
          </div>
          <div className="landing-season__visual" aria-hidden>
            <div className="landing-season__board">
              <div className="landing-season__board-head">Saison · Solo</div>
              {[1, 2, 3, 4, 5].map((n) => (
                <div key={n} className="landing-season__board-row">
                  <span>{n}</span>
                  <span className="landing-season__board-name">Leitstelle · · ·</span>
                  <span className="landing-season__board-pts">{1200 - n * 87}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </RevealSection>

      <RevealSection className="landing-incidents" aria-labelledby="landing-inc-h">
        <h2 id="landing-inc-h" className="landing-section-title">
          {L.incidentsTitle}
        </h2>
        <ul className="landing-incident-strip">
          {L.incidentsTeaser.map((inc) => (
            <li key={inc.code}>
              <span className="landing-inc-code">{inc.code}</span>
              <span className="landing-inc-label">{inc.label}</span>
              <span className="landing-inc-hint muted small">{inc.hint}</span>
            </li>
          ))}
        </ul>
      </RevealSection>

      <RevealSection id="entwickler" className="landing-dev" aria-labelledby="landing-dev-h">
        <h2 id="landing-dev-h" className="landing-section-title">
          {L.developerTitle}
        </h2>
        <p className="landing-dev__name">{L.developerName}</p>
        <p className="landing-dev__note muted">{L.developerNote}</p>
      </RevealSection>

      {!loggedIn && (
        <RevealSection id="konto" className="landing-auth-inline" aria-labelledby="landing-auth-h">
          <div className="landing-section-head">
            <h2 id="landing-auth-h" className="landing-section-title">
              {L.authTitle}
            </h2>
            <p className="landing-section-sub muted">{L.authSubtitle}</p>
          </div>
          <div className="landing-auth-inline__actions">
            <button type="button" className="btn primary" onClick={() => openAuth("login")}>
              Anmelden
            </button>
            <button type="button" className="btn" onClick={() => openAuth("register")}>
              Registrieren
            </button>
          </div>
        </RevealSection>
      )}

      <footer className="landing-foot">
        <a href="/LEGAL.md">Rechtliches (LEGAL.md)</a>
        <span className="muted"> · </span>
        <button type="button" className="linkish" onClick={() => useGame.getState().setPanel("legal")}>
          Impressum in der App
        </button>
        {loggedIn && session?.playAllowed && (
          <>
            <span className="muted"> · </span>
            <a href="/play?panel=saison">Saison-Ranking</a>
          </>
        )}
        <span className="muted"> · </span>
        <a href="/play">Zum Spiel</a>
        <span className="muted"> · {L.footNote}</span>
      </footer>

      {authOpen && (
        <div className="landing-auth-modal" role="dialog" aria-modal="true" aria-label="Anmeldung">
          <button type="button" className="landing-auth-modal__backdrop" aria-label="Schließen" onClick={closeAuth} />
          <div className="landing-auth-modal__panel">
            <button type="button" className="landing-auth-modal__close" aria-label="Schließen" onClick={closeAuth}>
              ✕
            </button>
            <AuthCard
              initialMode={authOpen}
              embedded
              onAuth={onAuthed}
              showHomeLink={false}
            />
          </div>
        </div>
      )}

      <LegalPanel />
    </div>
  );
}
